import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { humanizeApiError } from '@lapo/core';
import type { ContactView, ContactSuggestionView, ToastInput } from '@lapo/ui';
import type { ApiClient } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';
import { API_URL } from './config.js';

/**
 * Owns the Google Contacts directory + reconciliation review queue shown in the Contacts view — the
 * synced contact list, the recency rail, the "is this the same person?" suggestion queue, the in-memory
 * search box, and every action the Contacts view drives (sync, merge, confirm/dismiss a suggestion).
 * Behavior-preserving extraction from App.tsx: it loads the whole (curated) directory once when the
 * Contacts view (or the Contact Pictures headshot studio) becomes visible, then filters client-side —
 * no per-keystroke round trip.
 *
 * Deps are passed in (never reached out of App): `api`, the auth `session` (for uid gating), `pushToast`,
 * and `needsContacts` (App computes `view.kind === 'contacts' || (view.kind === 'integration' &&
 * view.id === 'contact-pictures')` and passes it, reproducing the inline effect's exact gate). `session`
 * null (signed out) keeps everything empty/local. `loadContacts` is returned so non-contacts consumers
 * (integration run refresh, headshot approve) can refresh the directory after they touch photos.
 */
export interface UseContactsDeps {
  api: ApiClient;
  session: AuthSession | null;
  pushToast: (t: ToastInput) => number;
  /** True when the Contacts view (or the Contact Pictures headshot studio) is the visible surface. */
  needsContacts: boolean;
}

export interface UseContacts {
  // State.
  contacts: ContactView[];
  recentContacts: ContactView[];
  contactSuggestions: ContactSuggestionView[];
  contactQuery: string;
  setContactQuery: (q: string) => void;
  contactsLoading: boolean;
  contactsSyncing: boolean;
  contactsError: string | undefined;
  /** The directory filtered by `contactQuery` (client-side; instant, no server call). */
  shownContacts: ContactView[];
  // Loaders + actions.
  loadContacts: () => Promise<void>;
  onSyncContacts: () => Promise<void>;
  onMergeContacts: (ids: string[]) => Promise<void>;
  onConfirmSuggestion: (suggestionId: string) => Promise<void>;
  onDismissSuggestion: (suggestionId: string) => Promise<void>;
}

export function useContacts({ api, session, pushToast, needsContacts }: UseContactsDeps): UseContacts {
  // Google Contacts directory + reconciliation review queue, shown when the sidebar's Contacts view
  // (view.kind === 'contacts') is active.
  const [contacts, setContacts] = useState<ContactView[]>([]);
  const [recentContacts, setRecentContacts] = useState<ContactView[]>([]);
  const [contactSuggestions, setContactSuggestions] = useState<ContactSuggestionView[]>([]);
  const [contactQuery, setContactQuery] = useState('');
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsSyncing, setContactsSyncing] = useState(false);
  const [contactsError, setContactsError] = useState<string | undefined>(undefined);

  // Track the live session in a ref so the async loaders can gate on the current identity without
  // re-creating on every session change (mirrors App's sessionRef guard).
  const sessionRef = useRef<AuthSession | null>(session);
  sessionRef.current = session;

  // Fetch the whole (curated) directory once; searching filters it in-memory (no per-keystroke round
  // trip). The list is already deduped/pruned server-side, so it's small enough to filter client-side.
  const loadContacts = useCallback(async () => {
    if (!API_URL || !sessionRef.current) return;
    setContactsLoading(true);
    setContactsError(undefined);
    try {
      const [list, suggestions] = await Promise.all([api.getContacts(), api.getContactSuggestions()]);
      setContacts(list);
      setContactSuggestions(suggestions);
      // Recency rail — best-effort; a slow/empty mailbox mustn't block the directory.
      api
        .getRecentContacts()
        .then(setRecentContacts)
        .catch(() => setRecentContacts([]));
    } catch (err) {
      setContactsError(`Couldn't load contacts: ${humanizeApiError(err)}`);
    } finally {
      setContactsLoading(false);
    }
  }, [api]);

  // Load once when the Contacts view opens (not on every keystroke). Also load for the Contact Pictures
  // headshot studio, which needs the directory to pick a target contact.
  useEffect(() => {
    if (!needsContacts || !session) return;
    void loadContacts();
  }, [needsContacts, session, loadContacts]);

  // Client-side filtering: instant, no server call.
  const shownContacts = useMemo(() => {
    const q = contactQuery.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        (c.displayName ?? '').toLowerCase().includes(q) ||
        (c.org ?? '').toLowerCase().includes(q) ||
        c.emails.some((e) => e.toLowerCase().includes(q)),
    );
  }, [contacts, contactQuery]);

  const onSyncContacts = useCallback(async () => {
    if (!API_URL) {
      pushToast({ tone: 'info', title: 'Contacts need an API', message: 'Set VITE_API_URL to sync contacts.' });
      return;
    }
    setContactsSyncing(true);
    try {
      const { contacts: count } = await api.syncContacts();
      await loadContacts();
      pushToast({ tone: 'success', message: `Synced ${count} contact${count === 1 ? '' : 's'}.` });
    } catch (err) {
      pushToast({ tone: 'error', title: 'Could not sync contacts', message: humanizeApiError(err) });
    } finally {
      setContactsSyncing(false);
    }
  }, [api, loadContacts, pushToast]);

  const onMergeContacts = useCallback(
    async (ids: string[]) => {
      try {
        await api.mergeContacts(ids);
        await loadContacts();
        pushToast({ tone: 'success', message: `Merged ${ids.length} contacts into one.` });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not merge contacts', message: humanizeApiError(err) });
      }
    },
    [api, loadContacts, pushToast],
  );

  const onConfirmSuggestion = useCallback(
    async (suggestionId: string) => {
      try {
        await api.confirmContactSuggestion(suggestionId);
        setContactSuggestions((s) => s.filter((x) => x.suggestionId !== suggestionId));
        pushToast({ tone: 'success', message: 'Linked to contact.' });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not link', message: humanizeApiError(err) });
      }
    },
    [api, pushToast],
  );

  const onDismissSuggestion = useCallback(
    async (suggestionId: string) => {
      try {
        await api.dismissContactSuggestion(suggestionId);
        setContactSuggestions((s) => s.filter((x) => x.suggestionId !== suggestionId));
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not dismiss', message: humanizeApiError(err) });
      }
    },
    [api, pushToast],
  );

  return {
    contacts,
    recentContacts,
    contactSuggestions,
    contactQuery,
    setContactQuery,
    contactsLoading,
    contactsSyncing,
    contactsError,
    shownContacts,
    loadContacts,
    onSyncContacts,
    onMergeContacts,
    onConfirmSuggestion,
    onDismissSuggestion,
  };
}
